import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, ErrorState, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import CopyButton from '@/components/admin/CopyButton';
import BroadcastDialog from '@/components/admin/BroadcastDialog';
import { BROADCAST_KIND_STYLES } from '@/shared/constants';
import { AUDIENCE_LABELS, KIND_HINTS, audienceOf } from '@/shared/audience';
import { BROADCAST_FIELDS } from '@/shared/fields';
import { committed, logActivity } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/broadcasts/:id', layout: 'owner', access: "profile === 'Admin_c'" };

const SHOWN = 40;
const SEND_EMAIL = import.meta.env.VITE_SEND_EMAIL;

/** BCC batches of 50 through the send-email function. Returns false to keep the confirm open. */
async function sendBroadcast(broadcast, list, onRecount) {
  const to = list.map((p) => p.email).filter(Boolean);
  let r;
  try {
    r = await sdk.functions.invoke(SEND_EMAIL, {
      mode: 'mail',
      to,
      subject: broadcast.Name,
      text: broadcast.body_c ?? '',
      batch: 'bcc',
    });
  } catch (e) {
    toast.error(e?.message || 'The broadcast did not go out');
    return false;
  }
  const v = r?.value;
  if (!r?.ok || v?.success === false || v?.error) {
    const message = v?.message || (typeof v?.error === 'string' ? v.error : v?.error?.message) || r?.error?.message;
    toast.error(message || 'The broadcast did not go out');
    return false;
  }
  const sent = Number(v?.sent || 0);
  if (!sent) {
    toast.error(v?.message || 'Nobody got it');
    return false;
  }
  logActivity('Email', 'Sent broadcast', broadcast.Name, `${sent} of ${to.length}`);
  if (v?.partial) toast.warning(v.message || `Sent to ${sent} of ${to.length}`);
  else toast.success(`Sent to ${sent} ${sent === 1 ? 'person' : 'people'}`, { description: 'Everyone is on BCC, so nobody sees the others.' });
  if (sent !== Number(broadcast.recipients_c || 0)) await onRecount(sent);
  return true;
}

function Recipients({ broadcast, onRecount }) {
  const { data: people, loading, error, run } = useFetch(
    () => audienceOf(broadcast.audience_c, broadcast.kind_c),
    [broadcast.audience_c, broadcast.kind_c],
  );

  if (loading && people === undefined) {
    return <p className="py-3 text-sm text-muted-foreground">Counting the audience…</p>;
  }
  if (error) return <ErrorState message={error} onRetry={run} />;

  const list = people ?? [];
  const emails = list.map((p) => p.email).join(', ');
  const mailto = `mailto:?bcc=${encodeURIComponent(emails)}&subject=${encodeURIComponent(broadcast.Name)}&body=${encodeURIComponent(
    broadcast.body_c ?? '',
  )}`;
  const differs = list.length !== Number(broadcast.recipients_c || 0);

  if (!list.length) {
    return <p className="py-3 text-sm text-muted-foreground">Nobody is in this audience right now.</p>;
  }

  return (
    <div className="py-2">
      <p className="text-sm">
        <span className="font-heading text-2xl">{list.length}</span>{' '}
        <span className="text-muted-foreground">{list.length === 1 ? 'person' : 'people'} in this audience today</span>
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <ConfirmAction
          label="Send to all"
          icon="Send"
          variant="default"
          title={`Email ${list.length} ${list.length === 1 ? 'person' : 'people'}?`}
          description="Goes out now through your email settings, on BCC in batches of 50. This can't be undone."
          confirmLabel="Send now"
          busyLabel="Sending…"
          onConfirm={() => sendBroadcast(broadcast, list, onRecount)}
        />
        <CopyButton text={emails} label="Copy emails for BCC" />
        <Button variant="outline" size="sm" asChild>
          <a href={mailto}>
            <ApperIcon name="Send" size={16} />
            Open in mail app
          </a>
        </Button>
        {differs && (
          <ConfirmAction
            label={`Set recipients to ${list.length}`}
            icon="RefreshCw"
            title="Update the recipient count?"
            description={`The record says ${Number(broadcast.recipients_c || 0)}. Use this after you send it to today's list.`}
            busyLabel="Saving…"
            onConfirm={() => onRecount(list.length)}
          />
        )}
      </div>
      <ul className="mt-4 divide-y divide-border text-sm">
        {list.slice(0, SHOWN).map((p) => (
          <li key={p.email} className="flex flex-wrap items-center justify-between gap-x-3 py-2">
            <span>{p.name}</span>
            <span className="text-muted-foreground">{p.email}</span>
          </li>
        ))}
      </ul>
      {list.length > SHOWN && <p className="mt-2 text-xs text-muted-foreground">And {list.length - SHOWN} more in the copied list.</p>}
    </div>
  );
}

export default function AdminBroadcastDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: broadcast, loading, error, run } = useFetch(
    () => sdk.table('broadcasts_c').select(BROADCAST_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/broadcasts" label="Back to Broadcasts" />;
  const gate = recordGate({ back, loading, record: broadcast, error, run, what: 'Broadcast' });
  if (gate) return gate;

  async function recount(n) {
    const res = await sdk.table('broadcasts_c').update({ Id: broadcast.Id, recipients_c: n });
    if (!committed(res, 'Recipient count updated')) return false;
    run();
    return true;
  }

  async function remove() {
    const res = await sdk.table('broadcasts_c').remove(broadcast.Id);
    if (!committed(res, 'Broadcast deleted')) return false;
    navigate('/admin/broadcasts');
    return true;
  }

  return (
    <div className="mx-auto max-w-5xl">
      {back}
      <PageHeader
        eyebrow={`Broadcast · ${formatLocalDate(broadcast.CreatedOn, DATE_FORMATS.WITH_TIME)}`}
        title={broadcast.Name}
        description={AUDIENCE_LABELS[broadcast.audience_c] ?? broadcast.audience_c}
        actions={
          <>
            <StatusBadge value={broadcast.kind_c} styles={BROADCAST_KIND_STYLES} />
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            <ConfirmDelete title="Delete this broadcast?" description="Only the record goes. Emails already sent stay sent." onConfirm={remove} />
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-4">
          <Panel title="Message" icon="Mail" bodyClassName="py-4" actions={<CopyButton text={broadcast.body_c} label="Copy" />}>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{broadcast.body_c}</p>
          </Panel>
          <Panel title="Who gets it" icon="Users">
            <p className="pt-2 text-xs text-muted-foreground">{KIND_HINTS[broadcast.kind_c]}</p>
            <Recipients broadcast={broadcast} onRecount={recount} />
          </Panel>
        </div>
        <Panel title="Details" icon="Info" className="self-start">
          <dl className="divide-y divide-border">
            <DetailRow label="Subject">{broadcast.Name}</DetailRow>
            <DetailRow label="Kind">{broadcast.kind_c}</DetailRow>
            <DetailRow label="Audience">{AUDIENCE_LABELS[broadcast.audience_c] ?? broadcast.audience_c}</DetailRow>
            <DetailRow label="Recipients">{Number(broadcast.recipients_c || 0)}</DetailRow>
            <DetailRow label="Sent by">{broadcast.sent_by_c}</DetailRow>
            <DetailRow label="Sent">{formatLocalDate(broadcast.CreatedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
          </dl>
        </Panel>
      </div>

      <BroadcastDialog open={editOpen} onOpenChange={setEditOpen} broadcast={broadcast} onSaved={run} />
    </div>
  );
}
